import ClassNames from 'classnames'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { getMergedFilterParams, getMergedFilterQueryString } from '../../../../core/queryString'
import { postService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { DateFilter, GuestFilter, PriceFilter, TypeFilter } from '../../../atoms'
import PostList from '../../../organisms/PostList'
import SidebarLayout from '../../_layouts/sidebarLayout'
import { Panel } from '../../../atoms'
declare var window: interfaces.IWindow

interface IProps {
  user: interfaces.IUser
  isSignedIn: boolean
  favorite: boolean
  posts: any
  meta: interfaces.IPager
  tags: IJsonResponse
  categories: IJsonResponse
  areas: IJsonResponse
  languages: IJsonResponse
  google: any
}

const urlParams = new URLSearchParams(window.location.search)
const FILTER_FIELDS = [
  'date',
  'min_price',
  'max_price',
  'guests',
  'category_ids',
  'area_ids',
  'tag_ids',
  'post_types',
  'language_ids',
]

const searchPost = async baseParams => {
  const keywords = urlParams.get('keywords')
  const params = { ...baseParams }

  if (keywords) {
    params.keywords = keywords
  }

  return postService.search(params)
}

const usePosts = ({ favorite, initialPosts = [], initialPagination = null }) => {
  const keyword = urlParams.get('keywords')
  const [loading, setLoading] = React.useState(false)
  const [posts, setPosts] = React.useState(initialPosts)
  const [pagination, setPagination] = React.useState(initialPagination)
  // Map非表示に、表示されてたときのboundsを保持しておく

  const getPostsByPage = React.useCallback(async page => {
    setLoading(true)
    const currentSearchParams = new URLSearchParams(window.location.search)
    const filterParams = {}
    FILTER_FIELDS.forEach(param => {
      if (currentSearchParams.has(param)) {
        filterParams[param] = currentSearchParams.get(param)
      }
      if (currentSearchParams.has(`${param}[]`)) {
        filterParams[param] = currentSearchParams.getAll(`${param}[]`)
      }
    })
    const result = favorite
      ? await postService.getFavorites({ page })
      : await searchPost({ ...filterParams, page })

    setPosts(result.posts)
    setPagination(result.pagination)
    setLoading(false)
  }, [])

  const handleOnFilterSubmit = React.useCallback(async filterValue => {
    setLoading(true)

    const filterParams = getMergedFilterParams(FILTER_FIELDS, filterValue)
    const filterQueryString = getMergedFilterQueryString(FILTER_FIELDS, filterParams)
    const result = await searchPost({ ...filterParams })

    setPosts(result.posts)
    setPagination(result.pagination)
    setLoading(false)
    history.replaceState(null, null, `?${filterQueryString}`)
  }, [])

  const toggleLike = (postId, like) => {
    const detailMarker = document.getElementById(`marker-detail-${postId}`)

    // Update like of the opened detail marker
    if (detailMarker) {
      const el = detailMarker.querySelector('.Marker_Favorite')

      if (!el) {
        return
      }

      if (like) {
        el.classList.add('liked')
      } else {
        el.classList.remove('liked')
      }
    }

    setPosts(currentPosts =>
      currentPosts.map(postItem => ({
        ...postItem,
        user_liked: postItem.id === postId ? like : postItem.user_liked,
      }))
    )
  }

  return {
    loading,
    setLoading,
    posts,
    toggleLike,
    getPostsByPage,
    pagination,
    handleOnFilterSubmit,
  }
}

export const PostSearch: React.FC<IProps> = props => {
  const { posts: initialPosts, pagination: initialPagination } = React.useMemo(
    () => postService.getPostsFromJson(props.posts.posts),
    []
  )
  const defaultKeyword = React.useMemo(() => urlParams.get('keywords'), [])
  Hooks.SetDocumentTitle(
    defaultKeyword || I18n.t(props.favorite ? 'top_nav.favorites' : 'generic.search')
  )

  const { loading, posts, toggleLike, getPostsByPage, pagination, handleOnFilterSubmit } = usePosts(
    {
      favorite: props.favorite,
      initialPosts,
      initialPagination,
    }
  )

  return (
    <SidebarLayout user={props.user} showSidebar={props.favorite ? true : false}>
      <Panel marginType="dashboard" noDecoration={true}>
        <PostSearchWrapper className="PostSearch">
          {!props.favorite && (
            <SearchHeader
              handleOnFilterSubmit={handleOnFilterSubmit}
              categories={props.categories}
              areas={props.areas}
              tags={props.tags}
              languages={props.languages}
            />
          )}

          <PostList
            loading={loading}
            title={count =>
              props.favorite
                ? I18n.t('post.result_favorite', { count })
                : I18n.t('post.result_search', { count })
            }
            getPostsByPage={getPostsByPage}
            isSignedIn={props.isSignedIn}
            favorite={props.favorite}
            posts={posts}
            toggleLike={toggleLike}
            meta={pagination}
            keyword={defaultKeyword}
            hasMap={false}
            user={props.user}
          />
        </PostSearchWrapper>
      </Panel>
    </SidebarLayout>
  )
}

interface ISearchHeaderProps {
  categories: IJsonResponse
  areas: IJsonResponse
  tags: IJsonResponse
  languages: IJsonResponse
  handleOnFilterSubmit(params: any): void
}

const POST_TYPES = [
  {
    id: 'private',
    name: 'Private',
  },
  {
    id: 'group',
    name: 'Group',
  },
]
const SearchHeader: React.FC<ISearchHeaderProps> = props => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = React.useState(false)
  const { data: tags } = postService.getDataFromJson(props.tags)
  const { data: areas } = postService.getDataFromJson(props.areas)
  const { data: categories } = postService.getDataFromJson(props.categories)
  const { data: languages } = postService.getDataFromJson(props.languages)

  const defaultKeywords = urlParams.get('keywords') || ''

  const [searchBoxIsOpen, setSearchBoxIsOpen] = React.useState(true)
  return (
    <SearchHeaderWrapper className={ClassNames('SearchHeader showFilter', { isFilterPanelOpen })}>
      <FilterList>
        <TypeFilter
          className="area_filter"
          name={I18n.t('generic.area')}
          field="area_ids"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
          types={areas}
        />
        <DateFilter
          name={I18n.t('date')}
          field="date"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
        />
        <GuestFilter
          name={I18n.t('generic.guest')}
          field="guests"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
        />
        {constants.PAYMENT_REQUIRED && (
          <PriceFilter
            name={I18n.t('generic.price')}
            field="price"
            setIsFilterPanelOpen={setIsFilterPanelOpen}
            handleOnSubmit={props.handleOnFilterSubmit}
          />
        )}
        <TypeFilter
          name={I18n.t('generic.type')}
          field="post_types"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
          types={POST_TYPES}
        />
        <TypeFilter
          name={I18n.t('generic.tag')}
          field="tag_ids"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
          types={tags}
        />
        <TypeFilter
          name={I18n.t('generic.category')}
          field="category_ids"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
          types={categories}
        />
        <TypeFilter
          name={I18n.t('post.language')}
          field="language_ids"
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          handleOnSubmit={props.handleOnFilterSubmit}
          types={languages}
        />
      </FilterList>
    </SearchHeaderWrapper>
  )
}

const SearchHeaderWrapper = styled.div`
  justify-content: space-between;
  padding: 16px 0;

  @media (min-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
    width: 1040px;
    margin: auto;
  }

  @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    display: none;
    &.showFilter {
      display: flex;
    }
  }

  &.isFilterPanelOpen {
    overflow: visible;
  }

  .SearchHeader_MapToggle {
    display: flex;
    align-items: center;
    @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
      display: none;
    }

    > span {
      margin-right: 12px;
      font-size: 14px;
    }
  }
`

const FilterList = styled.div`
  display: flex;
  gap: 4px;
  height: 32px;
  padding: 0;
  @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    ::-webkit-scrollbar {
      display: none;
    }
  }
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    padding: 0 16px;
  }
  @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    padding-bottom: 1em;
  }

  .isFilterPanelOpen & {
    overflow: visible;
  }

  > div {
    flex-shrink: 0;
  }
`

const PostSearchWrapper = styled.div`
  margin: 0 auto;

  &.PostSearch {
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      padding-bottom: 1em;
      overflow: auto;
    }
  }

  .Wrapper_NoResult {
    font-weight: bold;
  }

  .PostList {
    @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
      flex: 1;
    }
  }

  .PostList_Main {
    width: 768px;
    @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
      width: auto;
    }
  }
`

export default PostSearch
